import { useEffect, useRef } from 'react'
import type { AvatarPresentation, MotionSequence } from '../types'

const INITIAL_RESPONSIVE_RESOLVE_FRAMES = 30
const RESIZE_RESPONSIVE_RESOLVE_FRAMES = 6
const RESTART_TRANSITION_RESOLVE_FRAMES = 20

type ResolveResponsiveAvatarPlacement = (nextAvatarPresentation?: AvatarPresentation) => AvatarPresentation | undefined

type UseResolvedAvatarPresentationParams = {
  avatarPresentation?: AvatarPresentation
  restartKey?: MotionSequence['restartKey']
  resolveResponsiveAvatarPlacement: ResolveResponsiveAvatarPlacement
}

type ResolveForFrameResult = {
  presentation: AvatarPresentation | undefined
  isInitialResolveFrame: boolean
  isRestartTransitionFrame: boolean
}

export const useResolvedAvatarPresentation = ({
  avatarPresentation,
  restartKey,
  resolveResponsiveAvatarPlacement,
}: UseResolvedAvatarPresentationParams) => {
  const resolvedPresentationRef = useRef<AvatarPresentation | undefined>(undefined)
  const previousAvatarPresentationRef = useRef<AvatarPresentation | undefined>(undefined)
  const effectAvatarPresentationRef = useRef<AvatarPresentation | undefined>(undefined)
  const effectRestartKeyRef = useRef<MotionSequence['restartKey']>(restartKey)
  const hasInitializedFirstPresentationRef = useRef(false)
  const initialResolveFramesRef = useRef(INITIAL_RESPONSIVE_RESOLVE_FRAMES)
  const resizeResolveFramesRef = useRef(0)
  const restartTransitionResolveFramesRef = useRef(0)

  useEffect(() => {
    if (!avatarPresentation) return

    const isSameAvatarPresentation = effectAvatarPresentationRef.current === avatarPresentation
    const isSameRestartKey = effectRestartKeyRef.current === restartKey
    effectAvatarPresentationRef.current = avatarPresentation
    effectRestartKeyRef.current = restartKey

    /** avatarPresentation 切り替え時に、参照キャッシュを最新値へ同期する */
    resolvedPresentationRef.current = undefined
    previousAvatarPresentationRef.current = undefined

    if (!hasInitializedFirstPresentationRef.current) {
      hasInitializedFirstPresentationRef.current = true
      initialResolveFramesRef.current = INITIAL_RESPONSIVE_RESOLVE_FRAMES
    } else if (!isSameRestartKey) {
      initialResolveFramesRef.current = 0
      restartTransitionResolveFramesRef.current = RESTART_TRANSITION_RESOLVE_FRAMES
    } else if (!isSameAvatarPresentation) {
      initialResolveFramesRef.current = 0
    }

    resizeResolveFramesRef.current = 0
  }, [avatarPresentation, restartKey])

  const resolveForFrame = (shouldSnapPosition: boolean): ResolveForFrameResult => {
    const isInitialResolveFrame = initialResolveFramesRef.current > 0
    const isRestartTransitionFrame = restartTransitionResolveFramesRef.current > 0
    if (shouldSnapPosition) {
      resizeResolveFramesRef.current = RESIZE_RESPONSIVE_RESOLVE_FRAMES
    }
    const isResizeResolveFrame = resizeResolveFramesRef.current > 0

    /** 再計算は presentation 変更・resize snap・初期ウォームアップ中だけに限定する */
    const shouldRecalculatePresentation =
      previousAvatarPresentationRef.current !== avatarPresentation ||
      isResizeResolveFrame ||
      isInitialResolveFrame ||
      isRestartTransitionFrame

    if (shouldRecalculatePresentation) {
      /** left/right + px 配置など、responsive 解決済みの presentation をフレーム用に確定する */
      const resolvedPresentation = resolveResponsiveAvatarPlacement(avatarPresentation)
      if (resolvedPresentation) {
        resolvedPresentationRef.current = resolvedPresentation
        previousAvatarPresentationRef.current = avatarPresentation
      }

      /** 初期ウォームアップフレームを 1 つ進める */
      if (resolvedPresentation && initialResolveFramesRef.current > 0) {
        initialResolveFramesRef.current -= 1
      }

      if (resolvedPresentation && resizeResolveFramesRef.current > 0) {
        resizeResolveFramesRef.current -= 1
      }

      if (resolvedPresentation && restartTransitionResolveFramesRef.current > 0) {
        restartTransitionResolveFramesRef.current -= 1
      }
    }

    return {
      presentation: resolvedPresentationRef.current,
      isInitialResolveFrame,
      isRestartTransitionFrame,
    }
  }

  return {
    resolveForFrame,
  }
}
